import React, { useState, useEffect } from "react";
import { Modal, Glyphicon, Button, Collapse } from "react-bootstrap";
import { getCap, storeCap, getUserInformation, getUser, storeLatestUrls, getLatestUrls } from "../utils/Common";
import { loadCap, recordUserAction, getLatestStoredImages, getMonitors, getZones, patchZone } from "../utils/bedrockAPI";
import ZoneEditor from "./ZoneEditor";
import LoaderButton from "./LoaderButton";
import './ZonesModal.css';

var zoneEditorType="low";

export default function ZonesModal({ show, onHide, cap, setErrorMessage, setErrorStatus, setShowError, ...props }) {
    const [buttonSize, setButtonSize] = useState("large");
    const [isLoading, setIsLoading] = useState(true);
    const [imageUrls, setImageUrls] = useState(null);
    const [monitors, setMonitors] = useState(null);
    const [zones, setZones] = useState(null);
    const [newZones, setNewZones] = useState(null);
    const [w, setW] = useState(528);
    const [h, setH] = useState(360);
    const [zonesHelp, setZonesHelp] = useState(false);

    useEffect(() => {
        function updateLayout() {
            const mobile = window.innerWidth < 767;
            const stageMargin = 24;
            const aspect = 360 / 528;

            setButtonSize(mobile ? "small" : "large");

            if (mobile) {
                const modalPadding = 20;
                const bodyPadding = 20;
                const cellPadding = 8;
                const available = window.innerWidth - modalPadding - bodyPadding - cellPadding;
                const editorW = Math.min(297, available - stageMargin);
                setW(editorW);
                setH(Math.round(editorW * (203 / 297)));
            } else {
                const modalWidth = Math.min(window.innerWidth - 40, 1220);
                const bodyPadding = 48;
                const gridPadding = 32;
                const gridGap = 20;
                const cellPadding = 20;
                const gridInner = modalWidth - bodyPadding - gridPadding;
                const columnWidth = (gridInner - gridGap) / 2;
                const editorW = Math.floor(columnWidth - cellPadding - stageMargin);
                const cappedW = Math.min(528, Math.max(260, editorW));
                setW(cappedW);
                setH(Math.round(cappedW * aspect));
            }
        }
        updateLayout();
        window.addEventListener('resize', updateLayout);
        return () => window.removeEventListener('resize', updateLayout);
    }, []);

    useEffect(() => {
        async function accessCap() {
            try {
                setIsLoading(true);
                var tempCap = getCap();
                if(tempCap == null || tempCap.capId !== cap.capId) {
                    tempCap = await loadCap(cap.capId);
                    storeCap(tempCap);                    
                }
                setIsLoading(false);
            } catch(e) {
                console.log(e);
            }
        }
        if(cap && show) {
            accessCap();
            recordUserAction(getUser().userId,"zoneEditor",cap.capId,{'userData':getUserInformation()});
        }
    },[cap, show])

    useEffect(() => {
        async function accessMonitors() {
            const tempCams = await getMonitors(cap.capId);
            setMonitors(tempCams);
        }
        async function accessLastSavedImages() {
            try {
                var tempImageUrls = getLatestUrls();
                if(tempImageUrls==null) {
                    tempImageUrls = await getLatestStoredImages(cap.capId);
                    storeLatestUrls(tempImageUrls);
                }
                setImageUrls(tempImageUrls);
            } catch (e) {
                console.log(e);
            }
        }
        if(cap!=null && show) {
            setIsLoading(true);
            accessMonitors();
            accessLastSavedImages();
            if(cap.architecture.includes("JETSON")) {
                zoneEditorType="medium";
            } else {
                zoneEditorType="low";
            }
            setIsLoading(false);
        }
    },[cap, show])

    useEffect(() => {
        async function accessZones() {
            const tempZones = await getZones(cap.capId);
            var betterZones = {}
            var betterNewZones = {}
            for(var i=0; i<tempZones.length; i++) {
                betterZones[tempZones[i].monitorId] = tempZones[i];
                betterZones[tempZones[i].monitorId].changed = false;
                betterNewZones[tempZones[i].monitorId] = tempZones[i].zone.Coords;
            }
            setZones(betterZones);
            setNewZones(betterNewZones);
        }
        if(monitors!=null && show) {
            accessZones();
        }
    }, [monitors, show])

    function renderZoneEditor(zId,iId,t) {
        const updateZones = (monitorId,nodes) => {
            setNewZones({...newZones,[monitorId]:nodes});
        }

        function setSlider(v,id) {
            var newZone = zones[id];
            newZone.sensitivity = v;
            newZone.changed = true;
            setZones({...zones,[id]:newZone});
        }
        if(imageUrls && imageUrls[iId] && zones && zones[zId]) {
            return (
                <div className="zone-editor-cell">
                    <ZoneEditor
                        zone={zones[zId].zone.Coords}
                        url={imageUrls[iId].url}
                        id={zId}
                        w={w}
                        h={h}
                        type={t}
                        onChange={updateZones}
                    />
                </div>
            )
        } else {
            return null;    
        }
    }
    const saveZones = async () => {
        setIsLoading(true);
        let saved = false;
        for(const monitorId in newZones) {
            if(zones[monitorId]) {
                var newNewZone = zones[monitorId].zone;
                newNewZone.Coords = newZones[monitorId];
                var sensitivity = zones[monitorId].sensitivity;
                if(sensitivity < 70) sensitivity = 75;
                const newZone = {userId:getUser().userId,zone:newNewZone,sensitivity:sensitivity}
                const r = await patchZone(zones[monitorId].zoneId,newZone);
                if(r.status===204) {
                    saved = true;
                    setErrorMessage("Zones Saved!");
                    setErrorStatus("success-message");
                    setShowError(true);
                }
                var changedZone = zones[monitorId];
                changedZone.changed = false;
                setZones({...zones,[monitorId]:changedZone});
            }
        }
        setIsLoading(false);
        if (saved) {
            onHide();
        }
    }

    function renderZones() {

        return (
            <div className="zones-modal-content">
                {cap && (
                    <div className="zones-modal-subheader">
                        <div className="zones-modal-cap-row">
                            <div className="zones-modal-cap-name">
                                {cap.name}
                                {cap.neighborhood && <small> ({cap.neighborhood})</small>}
                            </div>
                            <Button
                                className="zones-modal-help-btn"
                                bsSize="small"
                                onClick={() => setZonesHelp(!zonesHelp)}
                            >
                                <Glyphicon glyph="info-sign" />&nbsp;&nbsp;Help
                            </Button>
                        </div>
                        <Collapse in={zonesHelp}>
                            <div className="zones-modal-help">
                                Zones represent where the AI looks for detections in the views.<br /><br />
                                We recommend keeping your zone tight to the area you care about.<br /><br />
                                Click and drag the nodes to adjust the zone. Tap the open circles to create new nodes. Double-tap closed circles to delete nodes.<br /><br />
                                Make sure you save your zones when you're done editing them.<br /><br />
                                Zones can't be edited if the cameras are off.
                            </div>
                        </Collapse>
                    </div>
                )}
                {monitors && imageUrls && zones && (
                    <div className={`zone-editors-grid ${cap.architecture && cap.architecture.includes('2CAM') ? 'zone-editors-2x1' : 'zone-editors-2x2'}`}>
                        {renderZoneEditor(monitors.house[zoneEditorType].monitorId,monitors.house.medium.monitorId,zoneEditorType)}
                        {renderZoneEditor(monitors.neighbor[zoneEditorType].monitorId,monitors.neighbor.medium.monitorId,zoneEditorType)}
                        {renderZoneEditor(monitors.street[zoneEditorType].monitorId,monitors.street.medium.monitorId,zoneEditorType)}
                        {renderZoneEditor(monitors.driveway[zoneEditorType].monitorId,monitors.driveway.medium.monitorId,zoneEditorType)}
                    </div>
                )}
            </div>
        );
    }

    return (
        <Modal show={show} onHide={onHide} bsSize="large" enforceFocus={false} className="zones-modal">
            <Modal.Header closeButton>
                <div className="zones-modal-title">Edit Motion Zones</div>
            </Modal.Header>
            <Modal.Body>
                {renderZones()}
            </Modal.Body>
            <Modal.Footer className="zones-modal-footer">
                <Button className="btn-brand-outline btn-compact zones-modal-cancel" onClick={onHide}>Cancel</Button>
                <LoaderButton
                    className="btn-brand-dark btn-compact zones-modal-save"
                    bsSize={buttonSize}
                    onClick={saveZones}
                    isLoading={isLoading}
                >
                    <Glyphicon glyph="floppy-disk" />&nbsp;&nbsp;{buttonSize === "large" ? "Save Zones" : "Save"}
                </LoaderButton>
            </Modal.Footer>
        </Modal>
    );
} 